/**
 * Shared offscreen tile compositor — export brief Modes 1 (PSD "Tile Reference")
 * and 2 (Wallpaper raster). Mirrors TileCanvas.vue's draw() sketch/trace
 * compositing (clipped per-placement sketch blit + colour tiling, trace drawn
 * once unclipped) so exports match what the artist actually sees on screen,
 * minus editor-only chrome (backgrounds, borders, node handles, ghost dimming).
 */
import { compose, scale as scaleMat, transformToMatrix, translation, type Mat } from '@/geometry/affine';
import type { TileTransform } from '@/patterns/types';
import { planLayerPlacement } from '@/services/layerPlacementBitmap';
import { tintCanvas } from '@/services/rasterTint';
import { TILE_RASTER, useLayersStore } from '@/stores/layers';
import { usePatternStore } from '@/stores/pattern';
import { useToolsStore } from '@/stores/tools';

/** Per-render tint memo: one tinted raster per (layer, colour, theme) blit
 *  `key`, reused across every placement in a single export. A large repeat
 *  count then reuses a fixed handful of tint canvases instead of allocating a
 *  fresh one per placement — the unbounded allocation that silently failed
 *  exports at high tile counts (iOS WKWebView canvas-memory budget). Mirrors
 *  TileCanvas.vue's per-frame liveTintCache; lives only for one render call. */
type TintCache = Map<string, HTMLCanvasElement>;

function tintCached(cache: TintCache, key: string, src: HTMLCanvasElement, hex: string): HTMLCanvasElement {
  let c = cache.get(key);
  if (!c) {
    c = tintCanvas(src, hex);
    cache.set(key, c);
  }
  return c;
}

/** Blit one layer at one placement into `ctx` (already positioned + clipped) —
 *  same rule as TileCanvas.vue via the shared planLayerPlacement (authored ink
 *  expands to one tinted blit per origin-class). Tints resolve through the
 *  caller's `cache` so they're shared across placements, not re-allocated. */
function blitLayerAt(ctx: OffscreenOrCanvasCtx, layerId: string, t: TileTransform, cache: TintCache): void {
  const tools = useToolsStore();
  const patternStore = usePatternStore();
  for (const b of planLayerPlacement(layerId, t, patternStore.currentConfig.wpgId, tools.colourCount, tools.colours)) {
    const img = b.hex ? tintCached(cache, b.key, b.canvas, b.hex) : b.canvas;
    ctx.drawImage(img, 0, 0, TILE_RASTER, TILE_RASTER, 0, 0, 1, 1);
  }
}

function polygonPath(ctx: OffscreenOrCanvasCtx, polygon: [number, number][]): void {
  ctx.moveTo(polygon[0]![0], polygon[0]![1]);
  for (let i = 1; i < polygon.length; i++) ctx.lineTo(polygon[i]![0], polygon[i]![1]);
  ctx.closePath();
}

type OffscreenOrCanvasCtx = CanvasRenderingContext2D;

/** Which raster layers a tiled render includes. */
export type RasterKind = 'sketch' | 'ink' | 'all';

/** Draw the requested raster layers for one placement, clipped + colour-tiled
 *  (needs the full TileTransform, not just its matrix, to classify colour).
 *  Stack order sketch (bottom) → ink (top); planPlacementBitmap leaves sketch
 *  untinted and colour-tiles ink, matching the live canvas. `kind` defaults to
 *  'all' so existing callers are unchanged. */
function blitRasterLayers(ctx: OffscreenOrCanvasCtx, t: TileTransform, cache: TintCache, kind: RasterKind = 'all'): void {
  const layers = useLayersStore();
  const list = kind === 'sketch' ? layers.sketchLayers : kind === 'ink' ? layers.inkLayers : layers.rasterLayers;
  for (const layer of list) {
    if (!layer.visible || layer.opacity <= 0) continue;
    // Region layers (PSD-imported paint whose bitmap legitimately extends
    // outside the fundamental polygon) get their own unclipped pass — see
    // renderWallpaperTiles's region pass below. Clipping/blitting them here
    // too would double-draw them AND truncate them to the polygon, which is
    // exactly the defect this pass exists to avoid.
    if (layers.getLayerImportRect(layer.id) !== undefined) continue;
    ctx.globalAlpha = layer.opacity;
    blitLayerAt(ctx, layer.id, t, cache);
  }
  ctx.globalAlpha = 1;
}

/**
 * Render the tiled, symmetry-transformed, colour-mapped neighbour cells into a
 * caller-supplied `ctx` under a caller-supplied `toPixels` transform (brief 07
 * §7) — the same per-placement clip+blit as `renderWallpaperTiles`, but drawn
 * into an existing canvas/coordinate system rather than its own auto-bbox, so
 * PSD export can fill its reference layers with surrounding context. `blitLayerAt`
 * maps TILE_RASTER²→[0,1]², so tinting per colour class stays correct.
 */
export function blitTiledInto(ctx: OffscreenOrCanvasCtx, toPixels: Mat, rings: number, kind: RasterKind = 'all'): void {
  const patternStore = usePatternStore();
  const polygon = patternStore.currentPattern.polygon();
  const cache: TintCache = new Map();
  for (const t of patternStore.currentVariant.transformsInRange(rings)) {
    const m = compose(toPixels, transformToMatrix(t));
    ctx.save();
    ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
    ctx.beginPath();
    polygonPath(ctx, polygon);
    ctx.save();
    ctx.clip();
    blitRasterLayers(ctx, t, cache, kind);
    ctx.restore();
    ctx.restore();
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

/** Final-render outline weight, in actual output pixels — deliberately NOT
 *  `l.strokeWidth` (a tile-space fraction that would render thicker at higher
 *  export resolutions instead of a crisp, resolution-independent line).
 *  `pxPerUnit` is the caller's tile-unit→pixel scale, so dividing by it here
 *  gives exactly this many pixels in the finished image regardless of size. */
const FINAL_TRACE_STROKE_PX = 2;

/** Outline mode (default) trace layers — stroked once, unclipped, wherever
 *  the caller's already-set transform puts them (matches the live canvas). */
function blitTraceLayers(ctx: OffscreenOrCanvasCtx, pxPerUnit: number): void {
  const layers = useLayersStore();
  for (const l of layers.traceLayers) {
    if (l.drawMode === 'solid') continue;
    if (!l.visible || l.opacity <= 0 || !l.d || l.stroke === 'none') continue;
    ctx.globalAlpha = l.opacity;
    const p = new Path2D(l.d);
    // §5: faint per-trace fill for a completed region (matches the live canvas).
    if (l.closed && l.fill) {
      ctx.fillStyle = l.fill;
      ctx.fill(p);
    }
    ctx.strokeStyle = l.stroke;
    ctx.lineWidth = FINAL_TRACE_STROKE_PX / pxPerUnit;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke(p);
    ctx.globalAlpha = 1;
  }
}

/** Solid Vector Line mode (brief 12-vector_draw) — a simple stroke (no
 *  fill, regardless of `l.fill`), replicated at every placement in
 *  `placements`, unclipped, matching the live canvas's rule. `toPixels`
 *  maps tile-space [0,1]² to the caller's pixel space; combined with each
 *  placement's own matrix for that copy's transform. */
function blitSolidTraceLayers(ctx: OffscreenOrCanvasCtx, toPixels: Mat, placements: TileTransform[]): void {
  const layers = useLayersStore();
  const solidLayers = layers.traceLayers.filter(
    (l) => l.drawMode === 'solid' && l.visible && l.opacity > 0 && l.d && l.stroke !== 'none',
  );
  if (solidLayers.length === 0) return;
  // toPixels is always a uniform scale + translation (no shear), so its
  // scale factor — and therefore pixels-per-tile-unit — is the length of
  // its transformed unit x-vector.
  const pxPerUnit = Math.hypot(toPixels[0], toPixels[1]);
  for (const t of placements) {
    const m = compose(toPixels, transformToMatrix(t));
    ctx.save();
    ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
    for (const l of solidLayers) {
      ctx.globalAlpha = l.opacity;
      ctx.strokeStyle = l.stroke;
      ctx.lineWidth = FINAL_TRACE_STROKE_PX / pxPerUnit;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(new Path2D(l.d));
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }
}

/** Every placement within the identity lattice cell (the kaleidoscope/stabilizer
 *  copies that make up ONE tile, e.g. the 6 rotations of a hexagon artist tile). */
function identityCellTransforms(): TileTransform[] {
  const patternStore = usePatternStore();
  return patternStore.currentVariant
    .transformsInRange(1)
    .filter((t) => Math.abs(t.tx) < 1e-9 && Math.abs(t.ty) < 1e-9);
}

/**
 * Render exactly one tile (tile-space [0,1]² → [0,sizePx]²): sketch layers
 * clipped+blitted per identity-cell placement (with colour tiling), trace
 * layers stroked once, unclipped, in identity space — the PSD "Tile Reference"
 * layer and the basis for the wallpaper renderer below.
 */
export function renderSingleTile(sizePx: number): HTMLCanvasElement {
  const patternStore = usePatternStore();
  const polygon = patternStore.currentPattern.polygon();
  const canvas = document.createElement('canvas');
  canvas.width = sizePx;
  canvas.height = sizePx;
  const ctx = canvas.getContext('2d')!;

  const cache: TintCache = new Map();
  for (const t of identityCellTransforms()) {
    const m = transformToMatrix(t);
    ctx.save();
    ctx.setTransform(sizePx * m[0], sizePx * m[1], sizePx * m[2], sizePx * m[3], sizePx * m[4], sizePx * m[5]);
    ctx.beginPath();
    polygonPath(ctx, polygon);
    ctx.save();
    ctx.clip();
    blitRasterLayers(ctx, t, cache);
    ctx.restore();
    ctx.restore();
  }

  ctx.setTransform(sizePx, 0, 0, sizePx, 0, 0);
  blitTraceLayers(ctx, sizePx);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  blitSolidTraceLayers(ctx, [sizePx, 0, 0, sizePx, 0, 0], identityCellTransforms());
  return canvas;
}

const MAX_WALLPAPER_DIM = 4096;

/**
 * Render a flat multi-tile wallpaper composite (Mode 2). `rings` drives the
 * wallpaper-group engine's own `transformsInRange` — the true placement set for
 * the active lattice, so skewed/hex/oblique groups tile correctly rather than
 * approximating a literal rows×columns rectangle (see ExportSheet's Wallpaper
 * panel for the UI-level rows/cols → rings mapping and rationale).
 */
export function renderWallpaperTiles(rings: number, pxPerUnit: number): HTMLCanvasElement {
  const patternStore = usePatternStore();
  const layersStore = useLayersStore();
  const tools = useToolsStore();
  const variant = patternStore.currentVariant;
  const polygon = patternStore.currentPattern.polygon();
  const placements = variant.transformsInRange(rings);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const t of placements) {
    const m = transformToMatrix(t);
    for (const [px, py] of polygon) {
      const x = m[0] * px + m[2] * py + m[4];
      const y = m[1] * px + m[3] * py + m[5];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  let density = pxPerUnit;
  const wantW = (maxX - minX) * density;
  const wantH = (maxY - minY) * density;
  if (wantW > MAX_WALLPAPER_DIM || wantH > MAX_WALLPAPER_DIM) {
    density *= MAX_WALLPAPER_DIM / Math.max(wantW, wantH);
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round((maxX - minX) * density));
  canvas.height = Math.max(1, Math.round((maxY - minY) * density));
  const ctx = canvas.getContext('2d')!;

  const toPixels = compose(scaleMat(density, density), translation(-minX, -minY));

  // Offset-row lattices (hex/oblique/rhombus) have a sheared basis, so the
  // square (n,m) selection above fills a PARALLELOGRAM — leaving empty
  // triangular gaps in two corners of the rectangular output canvas (the
  // "skewed parallelogram" export bug). The target rectangle stays the base
  // set's bbox (above); we just generate enough extra lattice cells to also
  // cover its corners, wrapping the row offset into a filled rectangle. Cells
  // that land off-canvas are clipped for free by the canvas bounds, and every
  // cell keeps its true latticeIndex so colour tiling stays correct. Non-offset
  // lattices (square/rectangular) already fill the rectangle, so `needed`
  // collapses back to `rings` and nothing extra is generated.
  let fillPlacements = placements;
  const basis = variant.latticeBasis;
  if (basis) {
    const [ax, ay] = basis.a;
    const [bx, by] = basis.b;
    const det = ax * by - ay * bx;
    if (Math.abs(det) > 1e-9) {
      let needed = rings;
      for (const [cx, cy] of [
        [minX, minY],
        [maxX, minY],
        [minX, maxY],
        [maxX, maxY],
      ] as const) {
        // World point cx,cy in lattice coordinates (α along a, β along b).
        const alpha = (by * cx - bx * cy) / det;
        const beta = (-ay * cx + ax * cy) / det;
        needed = Math.max(needed, Math.ceil(Math.abs(alpha)), Math.ceil(Math.abs(beta)));
      }
      needed += 1; // margin so edge cells' polygons fully overlap the border
      if (needed > rings) fillPlacements = variant.transformsInRange(needed);
    }
  }

  const cache: TintCache = new Map();
  for (const t of fillPlacements) {
    const m = compose(toPixels, transformToMatrix(t));
    ctx.save();
    ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
    ctx.beginPath();
    polygonPath(ctx, polygon);
    ctx.save();
    ctx.clip();
    blitRasterLayers(ctx, t, cache);
    ctx.restore();
    ctx.restore();
  }

  // Region pass (PSD-imported region paint) — unclipped, over the same
  // `placements` set computed above. The traced region is ONE element of the
  // tiling: its images under the variant's own stored placement set tile the
  // plane, and it legitimately pokes across the fundamental polygon's
  // boundary, so clipping it to the polygon the way ordinary rasters are
  // clipped would truncate it and leave the rest of the polygon unpainted;
  // drawing it at its own `rect` (getLayerImportRect), unclipped, avoids
  // that. Colour instances are N colourways of the SAME silhouette (all
  // painted at the colour-1 position); each placement draws exactly one,
  // selected by the placement's colour class — colours cycle through the
  // placement sequence per the export model.
  const regionLayers = layersStore.rasterLayers.filter(
    (l) => l.visible && l.opacity > 0 && layersStore.getLayerImportRect(l.id) !== undefined,
  );
  if (regionLayers.length > 0) {
    for (const t of fillPlacements) {
      const m = compose(toPixels, transformToMatrix(t));
      ctx.save();
      ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
      for (const layer of regionLayers) {
        const [rx0, ry0, rx1, ry1] = layersStore.getLayerImportRect(layer.id)!;
        ctx.globalAlpha = layer.opacity;
        // One draw per placement, instance selected by the placement's colour
        // class (planLayerPlacement) — colours cycle through the stored
        // placement sequence exactly as the export model defines: every
        // instance is painted at the SAME (colour-1) silhouette position and
        // the placement transform carries it to its cell.
        for (const b of planLayerPlacement(layer.id, t, patternStore.currentConfig.wpgId, tools.colourCount, tools.colours)) {
          const img = b.hex ? tintCached(cache, b.key, b.canvas, b.hex) : b.canvas;
          ctx.drawImage(img, 0, 0, img.width, img.height, rx0, ry0, rx1 - rx0, ry1 - ry0);
        }
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    }
  }

  // Outline-mode trace layers draw once, unclipped, at the identity tile —
  // matching TileCanvas.vue's live semantics (a traced shape "spills exactly
  // as drawn", it is not itself tiled per-placement). Solid-mode trace
  // layers (brief 12-vector_draw) instead replicate across every placement,
  // same as the raster layers above.
  const identity = compose(toPixels, [1, 0, 0, 1, 0, 0]);
  ctx.setTransform(identity[0], identity[1], identity[2], identity[3], identity[4], identity[5]);
  blitTraceLayers(ctx, density);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  blitSolidTraceLayers(ctx, toPixels, fillPlacements);

  return canvas;
}
